import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Lock, Ticket, X, Check } from 'lucide-react'
import { formatVND } from '@/lib/formatters'
import { useCart } from '../../hooks/useCart'
import { amountToFreeShipping } from '../../services/pricingService'
import { useCheckoutStore } from '@/modules/checkout'
import { useApplyVoucher, useVoucherStore } from '@/modules/vouchers-api'

interface CartSummaryProps {
  compact?: boolean
  onCheckoutClick?: () => void
}

export function CartSummary({ compact = false, onCheckoutClick }: CartSummaryProps) {
  const location = useLocation()
  const { items, totals } = useCart()
  const subtotalDiscounted = items.reduce((s, it) => s + it.price * it.qty, 0)
  const toFreeShip = amountToFreeShipping(subtotalDiscounted)
  const setStep = useCheckoutStore((s) => s.setStep)

  const voucher = useVoucherStore()
  const applyMut = useApplyVoucher()

  const [code, setCode] = useState('')
  const [err, setErr] = useState<string | null>(null)

  // Chỉ hiện ô voucher ở trang checkout
  const showVoucher = location.pathname === '/thanh-toan' || location.pathname === '/gio-hang'

  // Nếu subtotal thay đổi, re-validate voucher đã dùng
  useEffect(() => {
    if (voucher.code && subtotalDiscounted > 0) {
      applyMut.mutate({ code: voucher.code, subtotal: subtotalDiscounted }, {
        onSuccess: (res) => voucher.set(res.code, res.discount, res.description),
        onError: () => voucher.clear(),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotalDiscounted])

  const applyCode = async () => {
    const c = code.trim().toUpperCase()
    if (!c) return
    setErr(null)
    try {
      const res = await applyMut.mutateAsync({ code: c, subtotal: subtotalDiscounted })
      voucher.set(res.code, res.discount, res.description)
      setCode('')
    } catch (e: any) {
      setErr(e?.message ?? 'Mã không hợp lệ')
    }
  }

  const totalAfterVoucher = Math.max(0, totals.grandTotal - voucher.discount)

  return (
    <div className={compact ? '' : 'bg-surface border border-border rounded-xl shadow-card p-5'}>
      {!compact && <h3 className="font-semibold text-text mb-4">Tóm tắt đơn hàng</h3>}

      <div className="space-y-2.5 text-sm">
        <div className="flex items-center justify-between text-muted">
          <span>Tạm tính ({totals.itemCount} sản phẩm)</span>
          <span className="text-text font-medium">{formatVND(totals.subtotal)}</span>
        </div>
        {totals.discount > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-accent-600">Giảm giá SP</span>
            <span className="text-accent-600 font-medium">- {formatVND(totals.discount)}</span>
          </div>
        )}
        <div className="flex items-center justify-between text-muted">
          <span>Phí vận chuyển</span>
          <span className="text-text font-medium">
            {totals.shippingFee === 0 ? 'Miễn phí' : formatVND(totals.shippingFee)}
          </span>
        </div>
        {toFreeShip > 0 && items.length > 0 && (
          <div className="rounded-md bg-brand-50 border border-brand-100 px-3 py-2 text-xs text-brand-700">
            Mua thêm <span className="font-semibold">{formatVND(toFreeShip)}</span> để được miễn phí vận chuyển
          </div>
        )}

        {showVoucher && items.length > 0 && (
          <div className="pt-2">
            {voucher.code ? (
              <div className="rounded-md bg-accent-500/10 border border-accent-500/30 px-3 py-2.5 flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <Check className="w-4 h-4 text-accent-600 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-mono font-bold text-accent-700 text-sm">{voucher.code}</div>
                    {voucher.description && <div className="text-xs text-muted line-clamp-1">{voucher.description}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-semibold text-accent-700 text-sm">-{formatVND(voucher.discount)}</span>
                  <button onClick={() => voucher.clear()} className="w-7 h-7 rounded-md hover:bg-red-50 text-muted hover:text-danger flex items-center justify-center">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                    <input
                      value={code}
                      onChange={(e) => { setCode(e.target.value.toUpperCase()); setErr(null) }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyCode() } }}
                      placeholder="Nhập mã giảm giá"
                      className="w-full h-10 pl-9 pr-3 rounded-md border border-border bg-surface text-sm font-mono outline-none focus:border-brand-500 uppercase"
                    />
                  </div>
                  <button
                    onClick={applyCode}
                    disabled={!code.trim() || applyMut.isPending}
                    className="h-10 px-4 rounded-md bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white font-semibold text-sm whitespace-nowrap"
                  >
                    {applyMut.isPending ? '...' : 'Áp dụng'}
                  </button>
                </div>
                {err && <div className="text-xs text-danger mt-1.5">{err}</div>}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border-t border-border mt-4 pt-4 flex items-center justify-between">
        <span className="font-semibold text-text">Tổng cộng</span>
        <span className="text-brand-700 font-bold text-xl">{formatVND(totalAfterVoucher)}</span>
      </div>

      <Link
        to="/thanh-toan"
        onClick={(e) => {
          if (items.length === 0) { e.preventDefault(); return }
          setStep('review')
          onCheckoutClick?.()
        }}
        className={[
          'mt-5 w-full h-12 rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition',
          items.length === 0
            ? 'bg-slate-300 text-white cursor-not-allowed'
            : 'bg-brand-600 hover:bg-brand-700 text-white',
        ].join(' ')}
      >
        <Lock className="w-4 h-4" />
        Thanh toán ngay
      </Link>

      {!compact && (
        <Link to="/" className="mt-3 block text-center text-sm text-brand-600 hover:underline">
          Tiếp tục mua sắm
        </Link>
      )}
    </div>
  )
}
